// ESC/POS 指令拼装（纯字节，不依赖第三方打印机库）
// 中文用 GBK 编码（得力等国产热敏打印机内置字库默认 GBK）
import iconv from 'iconv-lite';
import { DEFAULT_COL_WIDTHS } from './receiptSettings';

function pushText(bytes, s) {
  const buf = iconv.encode(String(s), 'gbk');
  for (let i = 0; i < buf.length; i++) bytes.push(buf[i]);
}

// 显示宽度：ASCII = 1，中文 = 2
function displayWidth(s) {
  let w = 0;
  for (const ch of String(s)) {
    // eslint-disable-next-line no-control-regex
    w += /[\x00-\x7f]/.test(ch) ? 1 : 2;
  }
  return w;
}

function padLeft(s, w) {
  const cur = displayWidth(s);
  if (cur >= w) return s;
  return ' '.repeat(w - cur) + s;
}

function padRight(s, w) {
  const cur = displayWidth(s);
  if (cur >= w) return s;
  return s + ' '.repeat(w - cur);
}

/**
 * 清洗商品名：去掉括号及其内容（如 "中华（香烟）" → "中华"，"中华(软包)香烟" → "中华香烟"）
 */
export function cleanProductName(name) {
  let s = String(name || '');
  // 循环处理多次（处理 "A(B)C(D)E" 这种多重括号）
  s = s.replace(/\s*[（(][^）)]*[）)]\s*/g, ' ');
  return s.trim().replace(/\s+/g, '');
}

/**
 * 按列宽截断商品名（按显示宽度算）
 */
function clipName(name, width) {
  const s = String(name || '');
  if (displayWidth(s) <= width) return s;
  let out = '';
  let w = 0;
  const target = width - 1;
  for (const ch of s) {
    // eslint-disable-next-line no-control-regex
    const cw = /[\x00-\x7f]/.test(ch) ? 1 : 2;
    if (w + cw > target) break;
    out += ch;
    w += cw;
  }
  return out + '…';
}

/**
 * 生成小票的 ESC/POS 字节流
 * @param {Object} opts
 *   storeName    店名（标题，居中加大加粗）
 *   type         'sale' | 'purchase'
 *   typeLabel    类型标题（如 "销售单"/"出货单"，默认按 type 推断）
 *   date         日期字符串
 *   items        [{ name, quantity, price, subtotal }]
 *   total        小计汇总金额（合计）
 *   totalQuantity 数量汇总（所有商品总件数；可选，缺省自动累加）
 *   cols         { name, qty, price, sub } 列宽（可选）
 *   footer       页脚（默认 谢谢惠顾）
 */
export function buildReceiptBytes({
  storeName, type, typeLabel, date, items,
  total, totalQuantity, cols, footer
}) {
  const C = { ...DEFAULT_COL_WIDTHS, ...(cols || {}) };
  const totalW = C.name + C.qty + C.price + C.sub + 4; // +4 为 3 个分隔空格 + 1 安全
  const SEP = '-'.repeat(totalW);
  const b = [];
  const cmd = (...xs) => b.push(...xs);

  // 初始化 + 进入中文模式
  cmd(0x1b, 0x40);
  cmd(0x1c, 0x26);

  // 标题：店名
  cmd(0x1b, 0x61, 0x01);
  cmd(0x1b, 0x45, 0x01);
  cmd(0x1d, 0x21, 0x11);
  pushText(b, (storeName || '店铺名') + '\n');
  cmd(0x1d, 0x21, 0x00);
  cmd(0x1b, 0x45, 0x00);

  // 单据类型（用户自定义标题）
  const label = typeLabel || (type === 'purchase' ? '进货单' : '销售单');
  pushText(b, `── ${label} ──\n\n`);

  // 商品明细
  cmd(0x1b, 0x61, 0x01);
  pushText(b, '商品明细\n');
  cmd(0x1b, 0x61, 0x00);
  pushText(b, SEP + '\n');
  cmd(0x1b, 0x45, 0x01);
  pushText(b,
    padRight('种类', C.name) + ' ' +
    padLeft('数量', C.qty) + ' ' +
    padLeft('单价', C.price) + ' ' +
    padLeft('小计', C.sub) + '\n'
  );
  cmd(0x1b, 0x45, 0x00);
  pushText(b, SEP + '\n');

  // 商品明细 + 累加汇总
  let qtySum = 0;
  let subSum = 0;
  (items || []).forEach(it => {
    const cleanName = cleanProductName(it.name);
    const name = clipName(cleanName, C.name);
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const sub = Number(it.subtotal) || qty * price;
    qtySum += qty;
    subSum += sub;
    pushText(b,
      padRight(name, C.name) + ' ' +
      padLeft(String(qty), C.qty) + ' ' +
      padLeft(price.toFixed(1), C.price) + ' ' +
      padLeft(sub.toFixed(1), C.sub) + '\n'
    );
  });

  const finalQty = (Number(totalQuantity) || 0) || qtySum;
  const finalTotal = (Number(total) || 0) || subSum;

  pushText(b, SEP + '\n');

  // 汇总（右对齐 + 加粗）
  cmd(0x1b, 0x61, 0x02);
  cmd(0x1b, 0x45, 0x01);
  pushText(b, `数量汇总: ${finalQty} 件\n`);
  pushText(b, `小计汇总: ${finalTotal.toFixed(1)} 元\n`);
  cmd(0x1b, 0x45, 0x00);
  cmd(0x1b, 0x61, 0x00);

  // 日期 + 页脚（居中）
  cmd(0x1b, 0x61, 0x01);
  if (date) pushText(b, '\n' + String(date) + '\n');
  pushText(b, '\n' + (footer || '谢谢惠顾') + '\n\n\n');

  // 切纸
  cmd(0x1d, 0x56, 0x42, 0x00);

  return new Uint8Array(b);
}

/**
 * 预览用结构化数据
 */
export function buildReceiptPreview(opts) {
  const C = { ...DEFAULT_COL_WIDTHS, ...(opts.cols || {}) };
  const totalW = C.name + C.qty + C.price + C.sub + 4;

  const items = (opts.items || []).map(it => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const sub = Number(it.subtotal) || qty * price;
    const cleanName = cleanProductName(it.name);
    return {
      name: clipName(cleanName, C.name),
      nameFull: cleanName,
      quantity: qty,
      price: price.toFixed(1),
      subtotal: sub.toFixed(1)
    };
  });

  const qtySum = items.reduce((a, x) => a + x.quantity, 0);
  const subSum = items.reduce((a, x) => a + Number(x.subtotal), 0);

  return {
    storeName: opts.storeName || '店铺名',
    type: opts.type || 'sale',
    typeLabel: opts.typeLabel || (opts.type === 'purchase' ? '进货单' : '销售单'),
    date: opts.date || '',
    items,
    total: ((Number(opts.total) || 0) || subSum).toFixed(1),
    totalQuantity: ((Number(opts.totalQuantity) || 0) || qtySum),
    footer: opts.footer || '谢谢惠顾',
    sep: '-'.repeat(totalW),
    cols: C,
    totalWidth: totalW
  };
}

/**
 * 测试打印字节流
 */
export function buildTestBytes() {
  return buildReceiptBytes({
    storeName: '打印测试',
    type: 'sale',
    date: new Date().toLocaleString('zh-CN'),
    items: [
      { name: '可口可乐', quantity: 2, price: 3.0 },
      { name: '雪碧', quantity: 1, price: 3.5 },
      { name: '百事可乐（特价）', quantity: 3, price: 6.5 }
    ],
    total: 9.5,
    totalQuantity: 6,
    footer: '谢谢惠顾'
  });
}